import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { History, Loader2, Mail, CheckCheck } from "lucide-react";

const CAT_LABEL = {
  listini: "Listini prezzi",
  sconti: "Condizioni di vendita",
  spedizioni: "Spedizioni",
  generico: "Generico",
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso || "—"; }
};

/**
 * Admin: storico degli avvisi inviati ai Rivenditori Ufficiali con tracking di lettura.
 */
export function ResellerAlertsHistory({ className = "", testId = "alerts-history-btn" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalTargets, setTotalTargets] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reseller-notifications");
      setItems(data.notifications || []);
      setTotalTargets(data.total_targets || 0);
    } catch {
      toast.error("Caricamento storico avvisi fallito");
    } finally { setLoading(false); }
  };

  const openDialog = () => { setOpen(true); load(); };

  return (
    <>
      <Button type="button" variant="secondary" size="sm" onClick={openDialog} data-testid={testId}
        className={`rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover ${className}`}>
        <History className="mr-1.5 h-3.5 w-3.5" /> Storico avvisi
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="alerts-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Storico avvisi ai rivenditori</DialogTitle>
            <DialogDescription>
              Elenco degli avvisi pubblicati con il conteggio di quanti Rivenditori Ufficiali li hanno letti.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="alerts-history-empty">
              Nessun avviso inviato finora.
            </div>
          ) : (
            <div className="space-y-3" data-testid="alerts-history-list">
              {items.map((n) => {
                const total = n.total_targets ?? totalTargets;
                const read = n.read_count ?? 0;
                const pct = total > 0 ? Math.round((read / total) * 100) : 0;
                return (
                  <div key={n.id} data-testid="alert-history-row" className="rounded-xl border border-border bg-brema-bg p-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground">{n.title}</p>
                        {n.message && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.message}</p>}
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          <span className="rounded-full bg-brema-card px-2 py-0.5">{CAT_LABEL[n.category] || n.category}</span>
                          <span>{fmtDate(n.created_at)}</span>
                          {n.email_sent > 0 && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {n.email_sent} email</span>}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="flex items-center justify-end gap-1.5 text-sm font-bold text-foreground" data-testid="alert-read-count">
                          <CheckCheck className="h-4 w-4 text-positive" /> {read}{total > 0 ? `/${total}` : ""}
                        </div>
                        <p className="text-[11px] text-muted-foreground">letti</p>
                      </div>
                    </div>
                    {total > 0 && (
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brema-card">
                        <div className="h-full rounded-full bg-positive transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
